import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RemoteCustomerModal from '../components/RemoteCustomerModal';
import { supabase } from '../lib/supabase';
import { trackPageConversion } from '../lib/analytics';
import { Star as StarOutline, Gift, Banknote as BanknoteIcon, ArrowRight, ExternalLink, Users, Sparkles, Crown, ArrowUpRight, Bell, ChevronLeft, ChevronRight, HelpCircle, AlertCircle, Info, ChevronDown, ListFilter, X, Tag, Zap, Building2, Filter } from 'lucide-react';

const StarFilled = ({ className, size }: { className?: string, size?: number }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size || 24} 
    height={size || 24} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    className={className}
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

interface PageOrder {
  page: string;
  displayOrder: number;
}

interface Bank {
  id: string;
  name: string;
  logo: string;
  slogan: string;
  isSponsored: boolean;
  offers: string[];
  buttonText: string;
  color: string;
  mobileUrl?: string;
  redirectUrl?: string;
  displayOrder?: number;
  pageOrders?: PageOrder[];
  isActive?: boolean;
}

const banks: Bank[] = [
  {
    id: 'isbankasi',
    name: 'İş Bankası',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png',
    slogan: 'İş Bankası Müşterisi Ol!',
    isSponsored: false,
    offers: [
      '%0 Faizli 25.000 TL\'ye varan Taksitli Nakit Avans',
      '30.000 TL\'ye varan Ek Hesap!'
    ],
    buttonText: 'İş Bankası Müşterisi Ol!',
    color: 'blue',
    mobileUrl: 'https://www.isbank.com.tr/musteri-olmak-istiyorum',
    redirectUrl: 'https://www.isbank.com.tr/musteri-olmak-istiyorum',
    displayOrder: 5,
    isActive: true,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 5 },
      { page: 'konut-kredisi', displayOrder: 5 },
      { page: 'tasit-kredisi', displayOrder: 5 },
      { page: 'kobi-kredisi', displayOrder: 5 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 5 }
    ]
  },
  {
    id: 'yapikredi',
    name: 'Yapı Kredi',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/3b159da8-fe94-4b12-b9fa-4b9c60495305.png',
    slogan: 'Yapı Kredi Müşterisi Ol!',
    isSponsored: true,
    offers: [
      '%0 Faizli 27.000 TL\'ye varan Promosyon Fırsat!',
      '27.000 TL\'ye varan promosyon Fırsatı!'
    ],
    buttonText: 'Yapı Kredi Müşterisi Ol!',
    color: 'blue',
    redirectUrl: 'https://q2x3.tr.adj.st/www.yapikredi.com.tr/musteriol?adj_t=1ouhug3e_1oypqac2&adj_fallback=https%3A%2F%2Fwww.yapikredi.com.tr%2Fbanka-hesabi-ac%3Futm_source%3Dlidy_mevduat_dob&adj_redirect_macos=https%3A%2F%2Fwww.yapikredi.com.tr%2Fbanka-hesabi-ac%3Futm_source%3Dlidy_mevduat_dob&creative=test&adj_label=7',
    displayOrder: 6,
    isActive: true,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 6 },
      { page: 'konut-kredisi', displayOrder: 6 },
      { page: 'tasit-kredisi', displayOrder: 6 },
      { page: 'kobi-kredisi', displayOrder: 2 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 6 }
    ]
  },
  {
    id: 'garantibbva',
    name: 'Garanti BBVA',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png',
    slogan: 'Garanti BBVA Müşterisi Ol!',
    isSponsored: true,
    offers: [
      "Sigortasız, faizsiz 75.000 TL'ye varan fırsat!",
      "%43,50\'den başlayan oranlarla e-Vadeli Hesap!"
    ],
    buttonText: 'Garanti BBVA Müşterisi Ol!',
    color: 'green',
    redirectUrl: 'https://app.tr.adjust.com/17x0qx77_17mzqi6b?campaign=digital-onboarding-listeleme&adgroup=kredipark&creative=listeleme-onboarding&deep_link=https://m.garantibbva.com.tr/cs/dashboard%3Fcampaign%3Ddigital-onboarding-listeleme%26adgroup%3Dkredipark&tracker_limit=100000',
    displayOrder: 2,
    isActive: true,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 2 },
      { page: 'konut-kredisi', displayOrder: 1 },
      { page: 'tasit-kredisi', displayOrder: 2 },
      { page: 'kobi-kredisi', displayOrder: 1 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 2 }
    ]
  },
  {
    id: 'akbank',
    name: 'Akbank',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/7accd65f-06ae-48b1-875e-6d9827590be6.png',
    slogan: 'Akbank Müşterisi Ol!',
    isSponsored: true,
    offers: [
      '%0,99 Faizli 6 ay vadeli 100.000 TL Kredi Fırsatı!',
      '%0 faizli 25.000 TL\'ye varan Taksitli Avans!'
    ],
    buttonText: 'Akbank Müşterisi Ol!',
    color: 'red',
    mobileUrl: 'https://mza5.tr.adj.st/akbankdirekt?function=609&widget=true&adj_t=16qmi3f5&adj_deep_link=akbankdirekt%3A%2F%2Fsplash%2Ffunction%2F609&adj_campaign=tuzeldob_50k&adj_fallback=https%3A%2F%2Fwww.akbank.com%2Fbasvuru%2Fmobileappservice%2F%23',
    redirectUrl: 'https://mza5.tr.adj.st/akbankdirekt?function=609&widget=true&adj_t=16qmi3f5&adj_deep_link=akbankdirekt%3A%2F%2Fsplash%2Ffunction%2F609&adj_campaign=tuzeldob_50k&adj_fallback=https%3A%2F%2Fwww.akbank.com%2Fbasvuru%2Fmobileappservice%2F%23',
    displayOrder: 1,
    isActive: false,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 1 },
      { page: 'konut-kredisi', displayOrder: 2 },
      { page: 'tasit-kredisi', displayOrder: 1 },
      { page: 'kobi-kredisi', displayOrder: 3 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 1 }
    ]
  },
  {
    id: 'qnb',
    name: 'QNB',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/d9ae3539-f63d-4295-84e6-08dbe6e2a6d6.png',
    slogan: 'QNB Müşterisi Ol!',
    isSponsored: false,
    offers: [
      '60.000 TL ve 3 ay vadeye kadar %0 faizli ihtiyaç kredisi!',
      '%45,5\'a varan günlük mevduat faizi!'
    ],
    buttonText: 'QNB Müşterisi Ol!',
    color: 'blue',
    redirectUrl: 'https://at96.tr.adj.st?adj_t=18lzyxcn_18faw2ad&adj_deep_link=finansbankmobile%3A%2F%2F',
    displayOrder: 3,
    isActive: true,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 3 },
      { page: 'konut-kredisi', displayOrder: 3 },
      { page: 'tasit-kredisi', displayOrder: 3 },
      { page: 'kobi-kredisi', displayOrder: 3 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 3 }
    ]
  },
  {
    id: 'enpara',
    name: 'En Para',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/cb4abd40-216e-404a-9e2b-4dd7eda7462e.png',
    slogan: 'En Para Müşterisi Ol!',
    isSponsored: true,
    offers: [
      'Yeni Müşterilere %0 Faizli 60.000 TL\'ye Kadar Kredi!',
      'Masrafsız Bankacılık için Enparalı Olun!'
    ],
    buttonText: 'En Para Müşterisi Ol!',
    color: 'purple',
    redirectUrl: 'https://www.qnbfinansbank.enpara.com/bireysel-basvuru/dob-lp?ref=affiliate-kredipark-dob&utm_source=affiliate&utm_medium=kredipark&utm_campaign=dob',
    displayOrder: 4,
    isActive: false,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 4 },
      { page: 'konut-kredisi', displayOrder: 4 },
      { page: 'tasit-kredisi', displayOrder: 4 },
      { page: 'kobi-kredisi', displayOrder: 4 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 4 }
    ]
  },
  {
    id: 'nkolay',
    name: 'Nkolay',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png',
    slogan: 'Nkolay Müşterisi Ol!',
    isSponsored: true,
    offers: [
      '300.000 TL\'ye varan anında kredi fırsatı!',
      '%0 faizli 5.000 TL Kolay Nakit imkanı!'
    ],
    buttonText: 'Nkolay Müşterisi Ol!',
    color: 'blue',
    redirectUrl: 'https://app.tr.adjust.com/1hm92rld?campaign=DOB&fallback=https%3A%2F%2Fwww.nkolay.com%2Fnkolaymobilindir&redirect_macos=https%3A%2F%2Fwww.nkolay.com%2Fnkolaymobilindir',
    displayOrder: 7,
    isActive: true,
    pageOrders: [
      { page: 'ihtiyac-kredisi', displayOrder: 7 },
      { page: 'konut-kredisi', displayOrder: 7 },
      { page: 'tasit-kredisi', displayOrder: 7 },
      { page: 'kobi-kredisi', displayOrder: 7 },
      { page: 'uzaktan-musteri-edinimi', displayOrder: 7 }
    ]
  }
];

// Function to highlight monetary values with marker effect
const highlightMonetaryValues = (text: string, color: string) => {
  if (!text) return text;
  
  // Regular expression to match monetary values like "25.000 TL" or "1.500TL"
  const regex = /(\d+(?:\.\d+)?)\s*(?:TL|₺|TLL)/g;
  
  // Split the text by monetary values and keep the delimiters
  const parts = [];
  let lastIndex = 0;
  let match;
  
  // Create a new regex object to use with exec
  const regexObj = new RegExp(regex);
  
  // Find all matches and their positions
  while ((match = regexObj.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    
    // Add the match as a React element with light gray background
    parts.push(
      <span key={`money-${match.index}`} className="bg-slate-200 px-1 py-0.5 rounded font-semibold text-black">
        {match[0]}
      </span>
    );
    
    // Update lastIndex to continue after this match
    lastIndex = match.index + match[0].length;
  }
  
  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts;
};

const getBankColors = (color: string) => {
  switch (color) {
    case 'red':
      return {
        bg: 'bg-red-600',
        bgHover: 'hover:bg-red-700',
        bgGradient: 'bg-gradient-to-r from-red-500 to-red-600',
        text: 'text-red-600',
        border: 'border-2 border-red-600/20 hover:border-red-600',
        bgLight: 'bg-red-600/10',
        shadow: 'shadow-red-500/25',
        sponsorBg: 'bg-red-600'
      };
    case 'green':
      return {
        bg: 'bg-green-600',
        bgHover: 'hover:bg-green-700',
        bgGradient: 'bg-gradient-to-r from-green-500 to-green-600',
        text: 'text-green-600',
        border: 'border-2 border-green-600/20 hover:border-green-600',
        bgLight: 'bg-green-600/10',
        shadow: 'shadow-green-500/25',
        sponsorBg: 'bg-green-600'
      };
    case 'purple':
      return {
        bg: 'bg-purple-600',
        bgHover: 'hover:bg-purple-700',
        bgGradient: 'bg-gradient-to-r from-purple-500 to-purple-600',
        text: 'text-purple-600',
        border: 'border-2 border-purple-600/20 hover:border-purple-600',
        bgLight: 'bg-purple-600/10',
        shadow: 'shadow-purple-500/25',
        sponsorBg: 'bg-purple-600'
      };
    case 'blue':
    default:
      return {
        bg: 'bg-blue-600',
        bgHover: 'hover:bg-blue-700',
        bgGradient: 'bg-gradient-to-r from-blue-500 to-blue-600',
        text: 'text-blue-600',
        border: 'border-2 border-blue-600/20 hover:border-blue-600',
        bgLight: 'bg-blue-600/10',
        shadow: 'shadow-blue-500/25',
        sponsorBg: 'bg-blue-600'
      };
  }
};

const UzaktanMusteriEdinimi = () => {
  const navigate = useNavigate();
  const [selectedBanks, setSelectedBanks] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBankForModal, setSelectedBankForModal] = useState<Bank | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [banksList, setBanksList] = useState<Bank[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [gtmClasses, setGtmClasses] = useState<Record<string, string>>({});
  const currentPage = 'uzaktan-musteri-edinimi';

  const filterSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent;
      const isMobileDevice = /iPhone|iPod|Android/i.test(userAgent);
      const isTablet = /iPad/i.test(userAgent);
      setIsMobile(isMobileDevice || isTablet);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const [offersResult, trackingResult] = await Promise.all([
          supabase
            .from('bank_offers')
            .select('*')
            .eq('is_active', true)
            .order('display_order', { ascending: true }),
          supabase
            .from('page_conversion_tracking')
            .select('gtm_classes, enable_gtm')
            .eq('page_slug', '/uzaktan-musteri-edinimi')
            .eq('enable_gtm', true)
            .maybeSingle()
        ]);

        if (offersResult.error) {
          console.error('Error fetching bank offers:', offersResult.error);
          const activeBanks = banks.filter(bank => bank.isActive !== false);
          setBanksList(activeBanks);
        } else {
          const offerBanks = offersResult.data?.map((offer: any) => ({
            id: offer.bank_id,
            name: offer.bank_name,
            logo: offer.logo_url,
            slogan: offer.slogan,
            isSponsored: offer.is_sponsored,
            offers: offer.offers,
            buttonText: offer.button_text,
            color: offer.color,
            redirectUrl: offer.redirect_url,
            displayOrder: offer.display_order,
            isActive: offer.is_active,
            pageOrders: offer.page_orders
          })) || [];

          setBanksList(offerBanks.length > 0 ? offerBanks : banks.filter(bank => bank.isActive !== false));
        }

        if (trackingResult.data?.gtm_classes) {
          setGtmClasses(trackingResult.data.gtm_classes);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        const activeBanks = banks.filter(bank => bank.isActive !== false);
        setBanksList(activeBanks);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleBankToggle = (bankId: string) => {
    setSelectedBanks(prev =>
      prev.includes(bankId)
        ? prev.filter(id => id !== bankId)
        : [...prev, bankId]
    );
  };

  const pushGTMEvent = (bankName: string, bankId: string) => {
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'bank_button_click',
        bank_name: bankName,
        bank_id: bankId,
        page: 'uzaktan-musteri-edinimi'
      });
      console.log('GTM Event pushed:', { event: 'bank_button_click', bank_name: bankName, bank_id: bankId });
    }
  };

  const handleOpenModal = (bank: Bank) => {
    console.log('handleOpenModal called for bank:', bank.name);

    // Push GTM event
    pushGTMEvent(bank.name, bank.id);

    // If mobile/tablet, redirect directly
    if (isMobile) {
      const redirectUrl = bank.mobileUrl || bank.redirectUrl;
      if (redirectUrl) {
        try {
          // Call Google Ads conversion tracking
          if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
            console.log('Calling gtag_report_conversion for mobile');
            (window as any).gtag_report_conversion(redirectUrl);
          } else {
            console.log('gtag_report_conversion not available, opening URL directly');
            window.open(redirectUrl, '_blank', 'noopener,noreferrer');
          }
        } catch (error) {
          console.error('Error in mobile redirect:', error);
          window.open(redirectUrl, '_blank', 'noopener,noreferrer');
        }
      }
    } else {
      // If desktop, open modal
      console.log('Desktop detected, opening modal for:', bank.name);
      setSelectedBankForModal(bank);
      setIsModalOpen(true);
    }
  };

  // Filter banks based on selection
  const filteredBanks = selectedBanks.length > 0
    ? banksList.filter(bank => selectedBanks.includes(bank.id))
    : banksList;

  // Sort banks by page-specific display order
  const sortedBanks = [...filteredBanks].sort((a, b) => {
    // Get page-specific order if available
    const aOrder = a.pageOrders?.find(po => po.page === currentPage)?.displayOrder || a.displayOrder || 999;
    const bOrder = b.pageOrders?.find(po => po.page === currentPage)?.displayOrder || b.displayOrder || 999;
    return aOrder - bOrder;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#F7BA45]/20 to-slate-50 py-4 md:py-6">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-2 md:mb-3">
              Şubeye Gitmeden Banka Hesabı Açın
            </h1>
            <p className="text-slate-600 text-sm sm:text-base md:text-lg mb-4 md:mb-6">
              Kredipark ile şubeye gitmeden bankaları karşılaştır, seçtiğin kurumun anında müşterisi ol!
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto py-6 md:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            {/* Mobile Filter Toggle - Hidden completely */}
            <div className="hidden">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Filter size={20} className="text-[#F7BA45]" />
                  <span className="font-medium">Bankaları Filtrele</span>
                  {selectedBanks.length > 0 && (
                    <span className="bg-[#F7BA45] text-white text-sm px-2 py-0.5 rounded-full">
                      {selectedBanks.length}
                    </span>
                  )}
                </div>
                <ChevronDown 
                  size={20} 
                  className={`text-slate-400 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`}
                />
              </button>
            </div>

            {/* Bank Filter Sidebar - Hidden on mobile */}
            <div className={`lg:w-80 flex-shrink-0 ${isFilterOpen ? 'block' : 'hidden lg:block'}`}>
              <div className="sticky top-24 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-800">
                      <Building2 size={18} className="text-[#F7BA45]" />
                      <span className="font-medium">Bankalar</span>
                    </div>
                    {selectedBanks.length > 0 && (
                      <button
                        onClick={() => setSelectedBanks([])}
                        className="text-sm text-red-500 hover:text-red-600 font-medium flex items-center gap-1"
                      >
                        <X size={16} />
                        Temizle
                      </button>
                    )}
                  </div>
                </div>

                {/* Bank List */}
                <div className="p-4 space-y-2">
                  {banksList.map((bank) => (
                    <button
                      key={bank.id}
                      onClick={() => handleBankToggle(bank.id)}
                      className={`w-full px-3 py-2 rounded-lg text-sm text-left transition-colors flex items-center gap-3 ${
                        selectedBanks.includes(bank.id)
                          ? 'bg-[#F7BA45] text-white'
                          : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <img
                        src={bank.logo}
                        alt={bank.name}
                        className="w-8 h-8 object-contain rounded"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <span className="flex-1">{bank.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Bank Cards */}
            <div className="flex-1">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F7BA45] mx-auto mb-4"></div>
                    <p className="text-slate-600">Bankalar yükleniyor...</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6 md:space-y-4">
                  {sortedBanks.map((bank) => {
                  const colors = getBankColors(bank.color);
                  const pageOrder = bank.pageOrders?.find(po => po.page === currentPage)?.displayOrder || bank.displayOrder;
                  
                  return (
                    <div
                      key={bank.id}
                     className={`bg-white rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md ${colors.border} relative ${bank.id === 'enpara' ? 'hidden' : ''}`}
                    >
                      <div className="p-4">
                        {/* Bank Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                          <div
                            className="flex items-center gap-3 cursor-pointer group"
                            onClick={() => navigate(`/banka/${bank.id}`)}
                          >
                            <img
                              src={bank.logo}
                              alt={bank.name}
                              className="h-8 sm:h-10 object-contain rounded-lg group-hover:scale-105 transition-transform"
                            />
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-lg font-semibold text-slate-800 group-hover:text-[#F7BA45] transition-colors">{bank.name}</h3>
                                <div className="flex items-center gap-0.5">
                                  {[...Array(5)].map((_, index) => (
                                    <StarFilled
                                      key={index}
                                      size={14}
                                      className={colors.text}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div className="inline-flex items-center gap-1 text-xs">
                                <Bell size={12} className="text-slate-400" />
                                <span className="text-slate-500">
                                  Yeni müşterilere özel {bank.offers.length} fırsat
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Bank Content */}
                        <div className="space-y-5">
                          {/* All Offers */}
                          {bank.offers.map((offer, index) => (
                            <div key={index} className="relative">
                              <div className="rounded-xl p-2 border border-slate-200 hover:border-slate-300 transition-all duration-300">
                                <div className="absolute -top-2 right-3">
                                  <div className="relative border border-slate-300 bg-white text-slate-500 text-xs font-normal px-1.5 py-0.5 rounded-lg">
                                    <div className="flex items-center gap-1">
                                      <Zap size={10} className="text-slate-400" />
                                      <span className="text-xs">Fırsat {index + 1}</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-start gap-3 pt-3">
                                  <div className="p-2 rounded-xl bg-slate-100 flex-shrink-0">
                                    <Gift size={18} className="text-slate-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-base sm:text-lg font-normal text-slate-700">
                                      {highlightMonetaryValues(offer, bank.color)}
                                    </h3>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Action Button */}
                          <div className="relative group mt-6">
                            {isMobile ? (
                              <a
                                id={`remote-customer-${bank.id}`}
                                data-bank-name={bank.name}
                                href={bank.mobileUrl || bank.redirectUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                  pushGTMEvent(bank.name, bank.id);
                                  trackPageConversion('/uzaktan-musteri-edinimi');
                                }}
                                className={`${gtmClasses[bank.id] || ''} relative w-full px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-medium
                                  flex items-center justify-center gap-2 text-white shadow-lg cursor-pointer select-none no-underline
                                  ${colors.bgGradient} active:scale-[0.98] md:hover:shadow-xl md:hover:scale-[1.02] md:transition-all md:duration-300`}
                                style={{
                                  WebkitTapHighlightColor: 'transparent',
                                  WebkitTouchCallout: 'none',
                                  WebkitUserSelect: 'none',
                                  userSelect: 'none',
                                  touchAction: 'manipulation'
                                }}
                              >
                                <span>{bank.buttonText}</span>
                                <ArrowUpRight size={20} />
                              </a>
                            ) : (
                              <button
                                id={`remote-customer-${bank.id}`}
                                data-bank-name={bank.name}
                                onClick={() => {
                                  trackPageConversion('/uzaktan-musteri-edinimi');
                                  handleOpenModal(bank);
                                }}
                                className={`${gtmClasses[bank.id] || ''} relative w-full px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-medium
                                  flex items-center justify-center gap-2 text-white shadow-lg cursor-pointer select-none no-underline
                                  ${colors.bgGradient} active:scale-[0.98] md:hover:shadow-xl md:hover:scale-[1.02] md:transition-all md:duration-300`}
                                style={{
                                  WebkitTapHighlightColor: 'transparent',
                                  WebkitTouchCallout: 'none',
                                  WebkitUserSelect: 'none',
                                  userSelect: 'none',
                                  touchAction: 'manipulation'
                                }}
                              >
                                <span>{bank.buttonText}</span>
                                <ArrowUpRight size={20} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Remote Customer Modal */}
      {isModalOpen && selectedBankForModal && (
        <RemoteCustomerModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedBankForModal(null);
          }}
          bankName={selectedBankForModal.name}
          bankLogo={selectedBankForModal.logo}
          redirectUrl={selectedBankForModal.redirectUrl}
        />
      )}
    </div>
  );
};

export default UzaktanMusteriEdinimi;